import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import PageBackground from "@/components/PageBackground";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import ScrollReveal from "@/components/ScrollReveal";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

export interface FAQ {
  q: string;
  a: string;
}

export interface LandingPageProps {
  path: string;
  metaTitle: string;
  metaDescription: string;
  eyebrow: string;
  h1: React.ReactNode;
  intro: string;
  serviceName: string;
  serviceDescription: string;
  areaServed: string[];
  bullets: { title: string; text: string }[];
  ctaPrimary?: { label: string; to: string };
  ctaSecondary?: { label: string; to: string };
  faqs: FAQ[];
  internalLinks?: { label: string; to: string }[];
  extraSection?: React.ReactNode;
}

const BASE_URL = "https://4facemedia.de";

const LandingPage = ({
  path,
  metaTitle,
  metaDescription,
  eyebrow,
  h1,
  intro,
  serviceName,
  serviceDescription,
  areaServed,
  bullets,
  ctaPrimary = { label: "Projekt anfragen", to: "/kontakt" },
  ctaSecondary = { label: "Projekte ansehen", to: "/projekte" },
  faqs,
  internalLinks = [],
  extraSection,
}: LandingPageProps) => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: serviceName,
      provider: {
        "@type": "ProfessionalService",
        name: "4FaceMedia",
        url: BASE_URL,
        areaServed,
      },
      areaServed,
      description: serviceDescription,
      url: `${BASE_URL}${path}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: BASE_URL },
        { "@type": "ListItem", position: 2, name: serviceName, item: `${BASE_URL}${path}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <PageBackground />
      <div className="relative z-10">
      <SEO title={metaTitle} description={metaDescription} path={path} jsonLd={jsonLd} />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-hero">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <span className="text-primary text-sm font-medium tracking-wider uppercase">{eyebrow}</span>
            <h1 className="text-5xl md:text-7xl font-bold text-foreground mt-4 mb-6 leading-[1.05]">{h1}</h1>
            <p className="text-xl text-foreground/85 max-w-2xl mb-8">{intro}</p>
            <div className="flex flex-wrap gap-4">
              <Link
                to={ctaPrimary.to}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-md font-semibold hover:bg-primary/90 transition"
              >
                {ctaPrimary.label} <ArrowRight size={18} />
              </Link>
              <Link
                to={ctaSecondary.to}
                className="inline-flex items-center gap-2 border border-border px-6 py-3 rounded-md font-semibold hover:bg-card transition"
              >
                {ctaSecondary.label}
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Bullets */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {bullets.map((b, i) => (
              <ScrollReveal key={b.title} delay={i * 80}>
                <div className="p-8 rounded-2xl bg-gradient-card border border-border/50 hover:border-primary/20 transition h-full">
                  <Check size={20} className="text-primary mb-4" />
                  <h2 className="text-xl font-semibold text-foreground mb-3">{b.title}</h2>
                  <p className="text-foreground/85 leading-relaxed">{b.text}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {extraSection}

      {/* FAQ */}
      <section className="py-24 bg-card/30 border-y border-border">
        <div className="container mx-auto px-4 max-w-3xl">
          <ScrollReveal>
            <span className="text-primary text-sm font-medium tracking-wider uppercase">FAQ</span>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mt-3 mb-10">Häufige Fragen</h2>
          </ScrollReveal>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left text-lg">{f.q}</AccordionTrigger>
                <AccordionContent className="text-foreground/85 text-base leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Internal links */}
      {internalLinks.length > 0 && (
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-foreground mb-6">Weiterführende Themen</h2>
            <div className="flex flex-wrap gap-3">
              {internalLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="px-4 py-2 rounded-full border border-border hover:border-primary/40 text-foreground/85 hover:text-foreground transition"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-24">
        <div className="container mx-auto px-4 text-center">
          <ScrollReveal>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Bereit, dein nächstes Highlight zu inszenieren?
            </h2>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 rounded-md font-semibold hover:bg-primary/90 transition"
            >
              Projekt anfragen <ArrowRight size={18} />
            </Link>
          </ScrollReveal>
        </div>
      </section>

      <Footer />
      </div>
    </div>
  );
};

export default LandingPage;
